import { useMemo } from "react";
import { useT } from "../i18n";
import { dayHeader } from "../lib/dates";
import { agendaSort, dueDay, isLive } from "../lib/tasks";
import { useSyncState } from "../store";
import { updateTask } from "../sync/actions";
import { TaskRow } from "../tasks/TaskRow";
import { AddTask, EmptyState } from "../tasks/TaskTree";
import { DatePicker } from "../ui/pickers";
import { usePrefs } from "../ui/prefs";
import { useAnchor } from "../ui/primitives";
import { applySort, groupTasks, useCompletedTasks } from "./grouping";
import { SectionHeader } from "./sections";
import { useViewOptions, ViewOptionsButton } from "./viewOptions";

export function TodayView() {
  const state = useSyncState();
  const prefs = usePrefs();
  const t = useT();
  const o = useViewOptions("today", null);
  const resched = useAnchor();
  const { overdue, today } = useMemo(() => {
    const overdue = [];
    const today = [];
    for (const it of Object.values(state.items)) {
      if (!isLive(state, it)) continue;
      const d = dueDay(it, prefs.tz);
      if (!d) continue;
      if (d < prefs.today) overdue.push(it);
      else if (d === prefs.today) today.push(it);
    }
    return { overdue: agendaSort(overdue, prefs.tz), today: agendaSort(today, prefs.tz) };
  }, [state, prefs.tz, prefs.today]);
  const completed = useCompletedTasks(o.showCompleted ? { since_today: "1" } : null, 1).filter(
    (i) => i.completed_at && i.completed_at.slice(0, 10) >= prefs.today.slice(0, 10),
  );
  const count = overdue.length + today.length;
  const grouped = groupTasks(applySort([...overdue, ...today], o, prefs.tz), o.groupedBy, {
    state,
    tz: prefs.tz,
    today: prefs.today,
    lang: prefs.lang,
  });
  return (
    <div className="view" data-testid="today-view">
      <div className="view-header">
        <div>
          <h1 className="view-title">{t("today")}</h1>
          <div className="view-subtitle">
            {count} {t("tasks").toLowerCase()}
          </div>
        </div>
        <ViewOptionsButton
          viewType="today"
          objectId={null}
          layouts={["list"]}
          groupings={["none", "priority", "label", "project"]}
        />
      </div>
      {grouped ? (
        grouped.map((g) => (
          <div className="section" key={g.key}>
            <SectionHeader title={g.title} count={g.tasks.length} />
            {g.tasks.map((it) => (
              <TaskRow key={it.id} item={it} showProject />
            ))}
          </div>
        ))
      ) : (
        <>
          {overdue.length > 0 && (
            <div className="section" data-testid="overdue-group">
              <SectionHeader
                title={t("overdue")}
                count={overdue.length}
                extra={
                  <button type="button" className="reschedule" onClick={resched.open} data-testid="reschedule">
                    {t("reschedule")}
                  </button>
                }
              />
              {applySort(overdue, o, prefs.tz).map((it) => (
                <TaskRow key={it.id} item={it} showProject />
              ))}
            </div>
          )}
          <div className="section">
            <SectionHeader title={dayHeader(prefs.today, prefs.today, prefs.lang)} count={today.length} />
            {applySort(today, o, prefs.tz).map((it) => (
              <TaskRow key={it.id} item={it} showProject hideDate={!it.due || dueDayHasNoTime(it)} />
            ))}
            {completed.map((it) => (
              <TaskRow key={it.id} item={it} showProject />
            ))}
            <AddTask defaults={{ due: { date: prefs.today } }} />
          </div>
        </>
      )}
      {count === 0 && <EmptyState text={t("emptyToday")} />}
      {resched.anchor && (
        <DatePicker
          anchor={resched.anchor}
          onClose={resched.close}
          value={null}
          allowRecurring={false}
          onChange={(due) => {
            for (const it of overdue) {
              // Keep recurring tasks' recurrence: reschedule with a plain date only for non-recurring.
              if (due && "date" in due && it.due?.is_recurring)
                void updateTask(it.id, { due: { ...it.due, date: due.date } });
              else void updateTask(it.id, { due });
            }
          }}
        />
      )}
    </div>
  );
}

function dueDayHasNoTime(it: { due: { date: string } | null }): boolean {
  return Boolean(it.due && it.due.date.length === 10);
}
